import * as React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { ApplicationState } from '../../../store';
import * as SharedMaterialsManagementStateController from '../../../store/StateControllers/SharedMaterials/SharedMaterialsManagementStateController';
import * as Table from '../../core/ToxnotReactTable';
import * as Models from '../../../store/Models/SharedMaterials/DataModel';
import { ToxnotReactComponentPaths } from '../../core/ToxnotReactComponentPaths';
import { RouteComponentProps } from 'react-router';
import { LoadingSpinner, LoadingSpinnerSize } from '../../core/LoadingSpinner';
import { SlidingDrawer } from '../../core/SlidingDrawer';
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from '../../core/ToxnotReactModal';
import { ToxnotComponentProperties } from '../../core/BaseApplicationComponents/ToxnotComponent';
import { ToxnotSignInRequiredComponent } from '../../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';
import { ScrollableContent, TopActionButtons, TopActionsAndScrollableContentContainer, Header } from '../../core/TopActionsAndScrollableContentContainer';

import './MyPassports.css';
import Layout from '../../Layout';
import TowStarter, { DataSourceOptionType } from '../../../tow/TowStarter';
import { ToxnotServiceClient } from '../../../store/ToxnotServiceClient';

enum ImportState {
    Importing,
    Failed,
    Successful,
    HasLinkedMaterials
}


type MyPassportsLocationState = {
    responseId?: string;
    surveyResponseInventoryId?: string;
}


type MyPassportsProps =
    typeof SharedMaterialsManagementStateController.actionCreators &
    SharedMaterialsManagementStateController.ManagementState &
    ToxnotComponentProperties &
    RouteComponentProps<MyPassportsLocationState>;

interface MyPassportsState {
    passportIdsToDelete: { [id: string]: string };
    slidingDrawerLoading: boolean;
    responseId: string | undefined;
    surveyResponseInventoryId: string | undefined;
    importState: ImportState;
}

class MyPassports extends ToxnotSignInRequiredComponent<MyPassportsProps, MyPassportsState> {
    private _towStarter: TowStarter;
    private _serviceClient: ToxnotServiceClient;

    constructor(props: MyPassportsProps) {
        super(props);
        const locationState: MyPassportsLocationState | undefined = props.location?.state as MyPassportsLocationState;
        this._serviceClient = new ToxnotServiceClient();

        this.state = {
            passportIdsToDelete: {},
            slidingDrawerLoading: false,
            responseId: locationState?.responseId,
            surveyResponseInventoryId: locationState?.surveyResponseInventoryId,
            importState: ImportState.Importing
        };

        this._towStarter = new TowStarter();
    }
  
    // ===============================================================================

    public componentDidMount() {
        this.props.getAllAccessManagerData();
    }

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [];
    }

    // ===============================================================================

    private getUserFriendlyAccessLevelNames() {
        return {
            [Models.SharedMaterialAccessType.Private]: this.localizeString("Unlisted"),
            [Models.SharedMaterialAccessType.Protected]: this.localizeString("Restricted"),
            [Models.SharedMaterialAccessType.Public]: this.localizeString("Public")
        };
    }

    // ===============================================================================

    private isSelectingPassportForSurveyResponse = () => this.state.responseId !== undefined

    // ===============================================================================

    protected renderConcreteComponent() {
        const tableColumns: Table.TableColumn<Models.SharedMaterialCoreData>[] = [
            {
                value: this.localizeString("Name"),
                isSortable: true,
                getSortableValue: (model) => model.materialName
            },
            {
                value: this.localizeString("Category")
            },
            {
                value: this.localizeString("Access Level")
            }
        ];

        return (
            <Layout options={{ pinAppContentToWindowHeight: true }}>
                <TopActionsAndScrollableContentContainer shouldMakeTnSection={true} tnSectionIsRaw={false} customClassName={"my-passports-root"}>
                    <Header>
                        <div className="tn-section-row tn-section-heading-row">
                            <div className="tn-section-col-12">
                                <h1>{this.localizeString("Manage Your Digital Product Passports")}</h1>
                            </div>
                        </div>
                    </Header>
                    <TopActionButtons>
                        {this.getUserContextForSignedInUser().isEditor && !this.isSelectingPassportForSurveyResponse() &&
                            <div className="my-passport-top-actions-container">
                                <button className="create-passport-button btn btn-default" onClick={e => this.createPassportButton_click()}><span className="glyphicon glyphicon-plus" /> {this.localizeString("Create Passport")}</button>
                                <button className="delete-passports-button btn btn-default" onClick={e => this.deletePassportsButton_click(e)}><span className="glyphicon glyphicon-remove" /> {this.localizeString("Delete Passports")}</button>
                                <button id="grant-access-button" className="btn btn-default" onClick={e => this.groupAccessButton_click()}>{this.localizeString("Grant Permissions For Passports")}</button>
                            </div>
                        }
                        {this.getUserContextForSignedInUser().isEditor && this.isSelectingPassportForSurveyResponse() &&
                            <div className="my-passport-top-actions-container">
                                <button id="cancel-import" className="btn btn-default" onClick={e => this.cancelImportData_click()}>{this.localizeString("Cancel")}</button>
                                <button id="import-from-passport" className="btn btn-toxnot-primary" onClick={e => this.importPassportData_click()}>{this.localizeString("Import")}</button>
                            </div>
                        }
                    </TopActionButtons>
                    <ScrollableContent>
                        {this.props.accessManagerState.isLoading
                            ? <div className="text-center"><LoadingSpinner size={LoadingSpinnerSize.Medium} /></div>
                            : <div id="my-passports-table">
                                <Table.ToxnotReactTable<Models.SharedMaterialCoreData>
                                    allRowModels={Object.values(this.props.ownedSharedMaterialsMap)}
                                    headerRow={tableColumns}
                                    getRowForTable={this.createRowData}
                                    rowClickEventHandler={this.row_onClick}
                                    localizer={this.props.appServices}
                                    selectingRowOptions={{
                                        rowModelGetId: this.getSharedMaterialId,
                                        onRowSelected: this.updateSelectedMaterials,
                                        showOnlyOnHover: true,
                                        singleSelect: this.isSelectingPassportForSurveyResponse()
                                    }}
                                /></div>
                        }
                        <SlidingDrawer
                            openDrawer={this.props.accessManagerState.currentlyShowACLForSharedMaterialId !== null}
                            onClose={this.slidingDrawerOnClose}
                            drawerTitle={this.getDrawerTitle()}>
                            <div className="current-open-acl-container">
                                {this.props.accessManagerState.currentlyShowACLForSharedMaterialId !== null && this.renderSideBarForSharedMaterial(this.props.accessManagerState.currentlyShowACLForSharedMaterialId)}
                            </div>
                        </SlidingDrawer>
                        <ToxnotReactModal modalId={"warn-about-removing-from-catalogs-modal"} localizedTitle={this.localizeString("Warning")}>
                            <ToxnotReactModalBody>
                                <div className="alert alert-warning">
                                    <p>
                                        {this.localizeString("You are about to remove a passport from all buyers and catalogs.")}
                                    </p>
                                    <p>
                                        {this.localizeString("Are you sure you want to continue?")}
                                    </p>
                                </div>
                            </ToxnotReactModalBody>
                            <ToxnotReactModalFooter localizer={this.props.appServices} enabledCloseButton={false}>
                                <button className="btn btn-default" onClick={this.closeCatalogWarningModal_click}>{this.localizeString("Cancel")}</button>
                                <button className="btn btn-danger" onClick={this.removeFromCatalogsConfirm_click}>{this.localizeString("Yes, Remove")}</button>
                            </ToxnotReactModalFooter>
                        </ToxnotReactModal>
                        <ToxnotReactModal modalId={"warn-about-deleting-passports-modal"} localizedTitle={this.localizeString("Are you sure you want to delete?")}>
                            <ToxnotReactModalBody>
                                <div className="alert alert-warning">
                                    <p>
                                        {this.localizeString("You are about to delete passport(s). This will remove them from the Exchange and they will no longer show up when searched by manufacturers.")}
                                    </p>
                                    <p>
                                        {this.localizeString("Are you sure you want to continue?")}
                                    </p>
                                </div>
                            </ToxnotReactModalBody>
                            <ToxnotReactModalFooter localizer={this.props.appServices} enabledCloseButton={false}>
                                <button className="btn btn-default" onClick={this.closeDeletePassportsWarningModal_click}>{this.localizeString("Cancel")}</button>
                                <button className="btn btn-danger" onClick={this.deletePassportsButtonConfirm_click}>{this.localizeString("Yes, Delete")}</button>
                            </ToxnotReactModalFooter>
                        </ToxnotReactModal>
                        <ToxnotReactModal modalId={"import-data-passport-modal"}>
                            <ToxnotReactModalBody>
                                <div className={`alert ${this.getImportAlertClass()}`}>
                                    <p>{this.renderImportMessage()}</p>
                                </div>
                            </ToxnotReactModalBody>
                            {this.state.importState === ImportState.Failed && (
                                <ToxnotReactModalFooter localizer={this.props.appServices} enabledCloseButton={false}>
                                    <button className="btn btn-default" onClick={this.closePassportsWarningModal_click}>
                                        {this.localizeString("Cancel")}
                                    </button>
                                </ToxnotReactModalFooter>
                            )}
                        </ToxnotReactModal>

                    </ScrollableContent>
                </TopActionsAndScrollableContentContainer>
            </Layout>
        );
    }

    // ===============================================================================

    private slidingDrawerOnClose = () => {
        this.props.showAccessControlList(null);

        this.setState({
            passportIdsToDelete: { ...this.props.accessManagerState.currentlySelectedSharedMaterialIds }
        });
    }

    // ===============================================================================

    private getDrawerTitle(): string | JSX.Element {
        if (this.props.accessManagerState.currentlyShowACLForSharedMaterialId === null) {
            return this.localizeString("Please select a passport to see its permissions");
        }

        return (
            <>
                {this.props.ownedSharedMaterialsMap[this.props.accessManagerState.currentlyShowACLForSharedMaterialId].materialName}
            </>
        );
    }

    // ===============================================================================

    private createRowData = (rowData: Models.SharedMaterialCoreData, rowIndex: number): Table.TableRow => {
        const accessLevelToFriendlyName = this.getUserFriendlyAccessLevelNames();

        return {
            cols: [
                <div className="access-control-passport-name-col">
                    <div className="shared-material-name-with-data" data-shared-material-id={rowData.sharedMaterialId}>{rowData.materialName}</div>
                    <div className="view-passport-permissions-hint">{this.localizeString("Click to view details...")}</div>
                </div>,
                rowData.categories.join(", "),
                accessLevelToFriendlyName[rowData.accessLevel]
            ]
        }
    }

    // ===============================================================================

    private renderSideBarForSharedMaterial(sharedMaterialId: string) {
        const accessLevelToFriendlyName = this.getUserFriendlyAccessLevelNames();

        return (
            <>
                {this.getUserContextForSignedInUser().isEditor &&
                    <div className="my-passport-top-actions-container m-t-10">
                        <button className="edit-passport-button btn btn-default" onClick={e => this.editPassportButton_click()} disabled={this.state.slidingDrawerLoading}><span className="glyphicon glyphicon-pencil" /> {this.localizeString("Edit Passport")}</button>
                        <button className="delete-passports-button btn btn-default" onClick={e => this.deletePassportsButton_click(e)} disabled={this.state.slidingDrawerLoading}><span className="glyphicon glyphicon-remove" /> {this.localizeString("Delete Passport")}</button>
                    </div>}
                {this.state.slidingDrawerLoading ? <LoadingSpinner size={LoadingSpinnerSize.Medium} /> :
                    <div className="grouped-data">
                        <div className="group">
                            <div className="title">{this.localizeString("General Info")}</div>
                            <div className="content">
                                <div>
                                    <p>
                                        {this.localizeString("Exchange Permissions:")} <b>{accessLevelToFriendlyName[this.props.ownedSharedMaterialsMap[sharedMaterialId].accessLevel]}</b>
                                    </p>
                                    <p>
                                        {this.localizeString("Exchange URL:")} <Link to={`${ToxnotReactComponentPaths.SharedMaterialBaseDetailPath}/${sharedMaterialId}`}>{window.location.hostname + `${ToxnotReactComponentPaths.SharedMaterialBaseDetailPath}/${sharedMaterialId}`}</Link>
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="group">
                            <div className="title">{this.localizeString("Teams with Access")}</div>
                            <div className="content">
                                {this.renderACLForSharedMaterial(this.props.accessManagerState.bundle, sharedMaterialId)}
                            </div>
                        </div>
                        <div className="group">
                            <div className="title">{this.localizeString("Buyers/Catalogs with Access")}</div>
                            <div className="content">
                                {this.renderCatalogsWithAccess(this.props.accessManagerState.bundle, sharedMaterialId)}
                            </div>
                        </div>
                    </div>
                }
            </>
        );
    }

    // ===============================================================================

    private getStartPublishUrl = (title: string, sharedMaterialId: string) => <a href={"/Data/Inventory?publicInventoryId=" + this.props.ownedSharedMaterialsMap[sharedMaterialId].publicInventoryId}>{title}</a>

    // ===============================================================================

    private renderACLForSharedMaterial = (bundle: Models.AccessManagerBundle, currentlyShownSharedMaterialId: string) => {
        let aclRows: JSX.Element[] = [];

        const accessControlList = bundle.accessControlListMap[currentlyShownSharedMaterialId];

        if (!accessControlList || accessControlList.length === 0) {
            if (this.props.ownedSharedMaterialsMap[currentlyShownSharedMaterialId].accessLevel === Models.SharedMaterialAccessType.Public) {
                aclRows.push(<div className="acl-row" key={0}>{this.localizeString("Material is public, anyone can view")}</div>);
            }
            else if (this.props.ownedSharedMaterialsMap[currentlyShownSharedMaterialId].accessLevel === Models.SharedMaterialAccessType.Protected) {
                aclRows.push(<div className="acl-row" key={0}>{this.localizeString("No one can view restricted data")}</div>);
            }

            else {
                aclRows.push(<div className="acl-row" key={0}>{this.localizeString("No one can view")}</div>);
            }
        }

        else {
            aclRows = accessControlList.map(
                (acl, index) =>
                    <div key={index} className="acl-row">
                        <span className="material-name">{acl.userWithAccessName.trim() + `'s ${this.localizeString("Team")} (` + acl.userWithAccessEmail.trim() + ")"}</span>
                        {this.props.appServices.userContext?.isEditor && <button className="btn btn-sm btn-toxnot-danger" onClick={e => this.aclRemoveButton_onClick(acl.teamOwnerIdWithAccess, acl.sharedMaterialId)}><span className="glyphicon glyphicon-remove"></span></button>}
                    </div>
            );
        }

        return (
            <div className="acl-rows-container">
                {aclRows}
            </div>
        );
    }

    // ===============================================================================

    private renderCatalogsWithAccess = (bundle: Models.AccessManagerBundle, currentlyShownSharedMaterialId: string) => {
        let body: JSX.Element;

        if (this.props.accessManagerState.removingFromCatalogs) {
            body = (
                <div className="text-center"><LoadingSpinner size={LoadingSpinnerSize.Small} /></div>
            );
        }

        else if (!this.props.ownedSharedMaterialsMap[currentlyShownSharedMaterialId].fetchForCatalogs) {
            body = (
                <>
                    {this.localizeString("This passport is not shared to any buyers or catalogs. You can change this when you publish or edit your passport")}
                </>
            );
        }
        else {
            body = (
                <>
                    <div className="tn-partners-container">
                        <div className="tn-partner">
                            <img src="/images/homepage/customer-logos/google.png" />
                        </div>
                        <div className="tn-partner mindful-materials">
                            <img src="/images/mindful-materials-logo.png" />
                        </div>
                    </div>
                    <button role="button" className="btn btn-danger" onClick={this.removeFromCatalogs_click}>{this.localizeString("Remove from all catalogs")}</button>
                </>
            );
        }

        return body;
    }

    // ===============================================================================

    private updateSelectedMaterials = (selectedIds: { [id: string]: string }) => {
        let newlySelectedIds = selectedIds;

        // passport single select 
        if (this.isSelectingPassportForSurveyResponse()) {
            const selectedSharedMaterialIds = Object.keys(this.props.accessManagerState.currentlySelectedSharedMaterialIds);
            if (selectedSharedMaterialIds.length !== 0) {
                const key = selectedSharedMaterialIds[0];
                delete newlySelectedIds[key];
                this.props.updateSelectedMaterials(newlySelectedIds);
            }
        }

        this.props.updateSelectedMaterials(newlySelectedIds);
        this.setState({
            passportIdsToDelete: { ...newlySelectedIds }
        });
    }

    // ===============================================================================

    private getSharedMaterialId = (model: Models.SharedMaterialCoreData) => {
        return model.sharedMaterialId;
    }

    // ===============================================================================

    //
    // UI Events
    //

    // ===============================================================================

    private aclRemoveButton_onClick = (teamOwnerIdWithAccess: string, sharedMaterialId: string) => {
        if (!confirm(this.localizeString("Are you sure you want to remove this team from having access to this Material Passport?"))) {
            return;
        }

        this.props.removeFromAccessControlList(teamOwnerIdWithAccess, sharedMaterialId);
    }

    // ===============================================================================

    private groupAccessButton_click = () => {
        const keys = Object.keys(this.props.accessManagerState.currentlySelectedSharedMaterialIds);

        if (keys.length === 0) {
            alert(this.localizeString("You must select at least one material before granting access"));
            return;
        }

        this.props.history.push(ToxnotReactComponentPaths.GrantGroupAccessInput);
    }

    // ===============================================================================

    private importPassportData_click = async () => {
        const selectedSharedMaterialIds = Object.keys(this.props.accessManagerState.currentlySelectedSharedMaterialIds);

        if (selectedSharedMaterialIds.length === 0) {
            alert(this.localizeString("Please select at least one passport"));
            return;
        }

        const selectedSharedMaterialId = selectedSharedMaterialIds[0];
        this.props.updateSelectedMaterials({});
        const publicInventoryId = this.props.ownedSharedMaterialsMap[selectedSharedMaterialId].publicInventoryId;

        this.setState({
            importState: ImportState.Importing
        });
        $('#import-data-passport-modal').modal('show');
       
        const result = await this._serviceClient.getSourceInventoryIdForPublicInventoryId(publicInventoryId);
        const sourceInventoryId = result.data;

        const response = await this._serviceClient.dataCollectionResponseImportInventory(
             this.state.responseId ?? "",
             this.state.surveyResponseInventoryId ?? "",
             sourceInventoryId!
        );
        
        if (!response.success) {
            this.setState({
                importState: response.result === "HasLinkedMaterials" ? ImportState.HasLinkedMaterials : ImportState.Failed
            });
        } else {
            this.setState({
                importState: ImportState.Successful
            });
            $('#import-data-passport-modal').modal('hide');

            const redirectUrl = "/Surveys/EditSurveyResponse/" + this.state.responseId;
            this.props.history.push({
                pathname: redirectUrl,
                state: {
                    importFromPassport: true
                }
            });
        }
            
    }

    // ===============================================================================

    private cancelImportData_click = () => {
        this.props.updateSelectedMaterials({});
        this.props.history.goBack();
    }

    // ===============================================================================

    private row_onClick = (e: React.MouseEvent, model: Models.SharedMaterialCoreData) => {
        this.props.showAccessControlList(model.sharedMaterialId);
        this.setState({
            passportIdsToDelete: { [model.sharedMaterialId]: model.sharedMaterialId }
        });
    }

    // ===============================================================================

    private removeFromCatalogs_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        $('#warn-about-removing-from-catalogs-modal').modal('show');
    }

    // ===============================================================================

    private removeFromCatalogsConfirm_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();

        if (this.props.accessManagerState.currentlyShowACLForSharedMaterialId === null) {
            throw new Error("There must be a selected shared material id in order to remove it from catalogs");
        }

        this.props.removeSharedMaterialsFromCatalogs([this.props.accessManagerState.currentlyShowACLForSharedMaterialId]);

        $('#warn-about-removing-from-catalogs-modal').modal('hide');
    }

    // ===============================================================================

    private closeCatalogWarningModal_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        $('#warn-about-removing-from-catalogs-modal').modal('hide');
    }

    // ===============================================================================

    private deletePassportsButton_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        if (Object.keys(this.state.passportIdsToDelete).length === 0) {
            alert(this.localizeString("You must select one or more passports to delete."));
            return;
        }

        $('#warn-about-deleting-passports-modal').modal('show');
    }

    // ===============================================================================

    private deletePassportsButtonConfirm_click = async () => {
        this.props.deletePassports(Object.keys(this.state.passportIdsToDelete));
        $('#warn-about-deleting-passports-modal').modal('hide');
    }

    // ===============================================================================

    private closeDeletePassportsWarningModal_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        $('#warn-about-deleting-passports-modal').modal('hide');
    }

    // ===============================================================================

    private closePassportsWarningModal_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        $('#import-data-passport-modal').modal('hide');    }

    // ===============================================================================

    private renderImportMessage() {
        const { importState } = this.state;
        switch (importState) {
            case ImportState.Importing:
                return this.localizeString("Importing...");
            case ImportState.Successful:
                return this.localizeString("Import Successful");
            case ImportState.Failed:
                return this.localizeString("Sorry, there was a problem accessing the service. Please try again.");
            case ImportState.HasLinkedMaterials:
                return this.localizeString("Sorry, the selected passport contains linked materials. 3E Exchange currently does not support importing passport with linked materials. Please choose another passport.");
            default:
                return null; 
        }
    }

    // ===============================================================================

    private getImportAlertClass() {
        const { importState } = this.state;

        switch (importState) {
            case ImportState.Importing:
                return "alert-info";
            case ImportState.Successful:
                return "alert-success"; 
            case ImportState.Failed:
            case ImportState.HasLinkedMaterials:
                return "alert-warning"; 
            default:
                return ""; 
        }
    }

    // ===============================================================================

    private createPassportButton_click = async () => {
        if (document.getElementById("tow-cancel-button") !== null) {
            alert(this.localizeString("You must close the current workflow at the top of the page before you can create a new passport."));
            return;
        }

        const success = await this._towStarter.startPublishSmWorkflow(DataSourceOptionType.SelectSingle);

        if (!success) {
            this.props.appServices.displayApiErrorMessage();
        }
    }

    // ===============================================================================

    private editPassportButton_click = async () => {
        if (document.getElementById("tow-cancel-button") !== null) {
            alert(this.localizeString("You must close the current workflow at the top of the page before you can edit a passport."));
            return;
        }

        if (this.props.accessManagerState.currentlyShowACLForSharedMaterialId === null) {
            return;
        }

        this.setState({
            slidingDrawerLoading: true
        });

        const sharedMaterial = this.props.ownedSharedMaterialsMap[this.props.accessManagerState.currentlyShowACLForSharedMaterialId];

        const result = await new ToxnotServiceClient().getSourceInventoryIdForPublicInventoryId(sharedMaterial.publicInventoryId);

        if (!result.success || result.data === null) {
            this.props.appServices.displayApiErrorMessage();
            return;
        }

        const success = await this._towStarter.startPublishSmWorkflow(DataSourceOptionType.EditExisting, result.data);

        if (!success) {
            this.props.appServices.displayApiErrorMessage();
        }

        this.setState({
            slidingDrawerLoading: false
        });
    }
}

// ===============================================================================

export default  connect(
    (state: ApplicationState) => ({ ...state.sharedMaterialsManagementState, appServices: state.appServices }),
    SharedMaterialsManagementStateController.actionCreators // Selects which action creators are merged into the component's props
)(MyPassports as any);